export type SkillTokenParseMode = 'composer' | 'skill-prompt';

export interface ISkillSourceRange
{
    /** Inclusive UTF-16 source offset. */
    start: number;
    /** Exclusive UTF-16 source offset. */
    end: number;
}

export type ISkillTokenScanPart =
    | { kind: 'text'; text: string; range: ISkillSourceRange; }
    | { kind: 'skill'; skillId: number; range: ISkillSourceRange; };

export type SkillTokenSyntaxErrorCode =
    | 'INVALID_SKILL_ID'
    | 'UNCLOSED_SKILL_TOKEN'
    | 'UNOWNED_LEADING_TEXT';

export class SkillTokenSyntaxError extends Error
{
    constructor(
        public readonly code: SkillTokenSyntaxErrorCode,
        public readonly range: ISkillSourceRange,
        message: string,
    )
    {
        super(message);
        this.name = 'SkillTokenSyntaxError';
    }
}

export interface ISkillInvocationSegment
{
    skillId: number;
    inputText: string;
    tokenRange: ISkillSourceRange;
    inputRange: ISkillSourceRange;
}

export interface IParsedSkillTokenSource
{
    source: string;
    mode: SkillTokenParseMode;
    /** Plain text before the first invocation; only skill-prompt mode may own it. */
    leadingText: string;
    invocations: ISkillInvocationSegment[];
    parts: ISkillTokenScanPart[];
}

export interface ISerializableSkillInvocation
{
    skillId: number;
    inputText?: string;
}

export interface ISkillReferenceTarget
{
    id: number;
    title: string;
}

export interface ISkillReferenceIndex
{
    byId: ReadonlyMap<number, ISkillReferenceTarget>;
    byNormalizedTitle: ReadonlyMap<string, ISkillReferenceTarget>;
}

export interface ISkillPromptReference
{
    syntax: 'canonical' | 'legacy';
    skillId?: number;
    skillTitle?: string;
    legacyName?: string;
    resolved: boolean;
    range: ISkillSourceRange;
}

export interface INormalizedSkillPrompt
{
    text: string;
    references: ISkillPromptReference[];
}

export interface ISkillPromptInvocationSegment extends ISkillPromptReference
{
    inputText: string;
    inputRange: ISkillSourceRange;
}

export interface IParsedSkillPromptReferences
{
    leadingText: string;
    invocations: ISkillPromptInvocationSegment[];
}

const TOKEN_PREFIX = '[skill:';

function isEscaped(source: string, index: number): boolean
{
    let backslashes = 0;
    for (let cursor = index - 1; cursor >= 0 && source[ cursor ] === '\\'; cursor--)
    {
        backslashes++;
    }
    return backslashes % 2 === 1;
}

function delimiterRunLength(source: string, index: number): number
{
    const delimiter = source[ index ];
    let end = index + 1;
    while (source[ end ] === delimiter)
    {
        end++;
    }
    return end - index;
}

function fenceAt(source: string, index: number): { delimiter: '`' | '~'; length: number; } | null
{
    const delimiter = source[ index ];
    if ((delimiter !== '`' && delimiter !== '~') || isEscaped(source, index))
    {
        return null;
    }

    const lineStart = source.lastIndexOf('\n', index - 1) + 1;
    if (!/^[ \t]{0,3}$/.test(source.slice(lineStart, index)))
    {
        return null;
    }

    const length = delimiterRunLength(source, index);
    return length >= 3 ? { delimiter, length } : null;
}

function invalidSkillId(source: string, start: number, end: number): never
{
    throw new SkillTokenSyntaxError(
        'INVALID_SKILL_ID',
        { start, end },
        `Invalid canonical skill ID in ${ source.slice(start, end) }`,
    );
}

function normalizeSkillTitle(title: string): string
{
    return title.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function createSkillReferenceIndex(
    skills: Iterable<ISkillReferenceTarget>,
): ISkillReferenceIndex
{
    const byId = new Map<number, ISkillReferenceTarget>();
    const byNormalizedTitle = new Map<string, ISkillReferenceTarget>();

    for (const skill of skills)
    {
        byId.set(skill.id, skill);
        const normalizedTitle = normalizeSkillTitle(skill.title);
        if (!byNormalizedTitle.has(normalizedTitle))
        {
            byNormalizedTitle.set(normalizedTitle, skill);
        }
    }

    return { byId, byNormalizedTitle };
}

interface ILegacySkillReference
{
    name: string;
    range: ISkillSourceRange;
}

function scanLegacySkillReferences(source: string): ILegacySkillReference[]
{
    const references: ILegacySkillReference[] = [];
    let lineStart = 0;
    let fence: { delimiter: '`' | '~'; length: number; } | null = null;

    while (lineStart <= source.length)
    {
        const newline = source.indexOf('\n', lineStart);
        const lineEndWithCarriageReturn = newline < 0 ? source.length : newline;
        const lineEnd = source[ lineEndWithCarriageReturn - 1 ] === '\r'
            ? lineEndWithCarriageReturn - 1
            : lineEndWithCarriageReturn;
        const line = source.slice(lineStart, lineEnd);
        const firstContentOffset = line.search(/\S/);
        const markerIndex = firstContentOffset < 0 ? lineStart : lineStart + firstContentOffset;
        const fenceCandidate = firstContentOffset < 0 ? null : fenceAt(source, markerIndex);

        if (fence)
        {
            if (fenceCandidate?.delimiter === fence.delimiter && fenceCandidate.length >= fence.length)
            {
                fence = null;
            }
        }
        else if (fenceCandidate)
        {
            fence = fenceCandidate;
        }
        else
        {
            const match = /^[ \t]*\/skill:(.*)$/.exec(line);
            const name = match?.[ 1 ].trim() ?? '';
            if (name)
            {
                references.push({ name, range: { start: lineStart, end: lineEnd } });
            }
        }

        if (newline < 0)
        {
            break;
        }
        lineStart = newline + 1;
    }

    return references;
}

interface ISourceReplacement
{
    range: ISkillSourceRange;
    text: string;
}

function applySourceReplacements(source: string, replacements: ISourceReplacement[]): string
{
    return replacements
        .sort((left, right) => right.range.start - left.range.start)
        .reduce((text, replacement) =>
            text.slice(0, replacement.range.start) + replacement.text + text.slice(replacement.range.end),
        source);
}

/** Resolves canonical IDs and legacy whole-line calls from one preloaded skill index. */
export function normalizeSkillPromptReferences(
    source: string,
    index: ISkillReferenceIndex,
): INormalizedSkillPrompt
{
    const legacyReferences = scanLegacySkillReferences(source);
    const canonicalParts = scanSkillTokens(source).filter(
        (part): part is Extract<ISkillTokenScanPart, { kind: 'skill'; }> => part.kind === 'skill',
    );
    const references: ISkillPromptReference[] = [];
    const replacements: ISourceReplacement[] = [];

    for (const legacy of legacyReferences)
    {
        const skill = index.byNormalizedTitle.get(normalizeSkillTitle(legacy.name));
        references.push({
            syntax: 'legacy',
            skillId: skill?.id,
            skillTitle: skill?.title,
            legacyName: legacy.name,
            resolved: !!skill,
            range: legacy.range,
        });
        if (skill)
        {
            replacements.push({ range: legacy.range, text: serializeSkillToken(skill.id) });
        }
    }

    for (const canonical of canonicalParts)
    {
        if (legacyReferences.some(legacy =>
            canonical.range.start >= legacy.range.start && canonical.range.end <= legacy.range.end
        ))
        {
            continue;
        }
        const skill = index.byId.get(canonical.skillId);
        references.push({
            syntax: 'canonical',
            skillId: canonical.skillId,
            skillTitle: skill?.title,
            resolved: !!skill,
            range: canonical.range,
        });
    }

    references.sort((left, right) => left.range.start - right.range.start);
    return { text: applySourceReplacements(source, replacements), references };
}

/** Segments canonical and legacy nested references using their positions in the original prompt. */
export function parseSkillPromptReferences(
    source: string,
    index: ISkillReferenceIndex,
): IParsedSkillPromptReferences
{
    const { references } = normalizeSkillPromptReferences(source, index);
    if (references.length === 0)
    {
        return { leadingText: source.trim(), invocations: [] };
    }

    return {
        leadingText: source.slice(0, references[ 0 ].range.start).trim(),
        invocations: references.map((reference, referenceIndex) =>
        {
            const inputRange = {
                start: reference.range.end,
                end: references[ referenceIndex + 1 ]?.range.start ?? source.length,
            };
            return {
                ...reference,
                inputText: source.slice(inputRange.start, inputRange.end).trim(),
                inputRange,
            };
        }),
    };
}

/** Projects canonical IDs to current titles for legacy v1/v2 clients. */
export function projectCanonicalSkillReferences(
    source: string,
    index: ISkillReferenceIndex,
): INormalizedSkillPrompt
{
    const references: ISkillPromptReference[] = [];
    const replacements: ISourceReplacement[] = [];

    for (const part of scanSkillTokens(source))
    {
        if (part.kind !== 'skill')
        {
            continue;
        }
        const skill = index.byId.get(part.skillId);
        references.push({
            syntax: 'canonical',
            skillId: part.skillId,
            skillTitle: skill?.title,
            resolved: !!skill,
            range: part.range,
        });
        if (skill)
        {
            replacements.push({ range: part.range, text: `/skill:${ skill.title }` });
        }
    }

    return { text: applySourceReplacements(source, replacements), references };
}

/**
 * Scans canonical skill tokens without interpreting token-shaped text in Markdown code
 * spans/fences or behind an odd number of escape backslashes.
 */
export function scanSkillTokens(source: string): ISkillTokenScanPart[]
{
    const parts: ISkillTokenScanPart[] = [];
    let textStart = 0;
    let cursor = 0;
    let inlineCodeLength = 0;
    let fence: { delimiter: '`' | '~'; length: number; } | null = null;

    while (cursor < source.length)
    {
        const fenceCandidate = fenceAt(source, cursor);
        if (fence)
        {
            if (
                fenceCandidate?.delimiter === fence.delimiter
                && fenceCandidate.length >= fence.length
            )
            {
                fence = null;
                cursor += fenceCandidate.length;
            }
            else
            {
                cursor++;
            }
            continue;
        }

        if (fenceCandidate && inlineCodeLength === 0)
        {
            fence = fenceCandidate;
            cursor += fenceCandidate.length;
            continue;
        }

        if (source[ cursor ] === '`' && !isEscaped(source, cursor))
        {
            const runLength = delimiterRunLength(source, cursor);
            if (inlineCodeLength === 0)
            {
                inlineCodeLength = runLength;
            }
            else if (runLength === inlineCodeLength)
            {
                inlineCodeLength = 0;
            }
            cursor += runLength;
            continue;
        }

        if (
            inlineCodeLength === 0
            && source.slice(cursor, cursor + TOKEN_PREFIX.length) === TOKEN_PREFIX
            && !isEscaped(source, cursor)
        )
        {
            const close = source.indexOf(']', cursor + TOKEN_PREFIX.length);
            if (close < 0)
            {
                throw new SkillTokenSyntaxError(
                    'UNCLOSED_SKILL_TOKEN',
                    { start: cursor, end: source.length },
                    'Canonical skill token is missing a closing bracket',
                );
            }

            const end = close + 1;
            const idText = source.slice(cursor + TOKEN_PREFIX.length, close);
            if (!/^[1-9]\d*$/.test(idText))
            {
                invalidSkillId(source, cursor, end);
            }

            const skillId = Number(idText);
            if (!Number.isSafeInteger(skillId))
            {
                invalidSkillId(source, cursor, end);
            }

            if (textStart < cursor)
            {
                parts.push({
                    kind: 'text',
                    text: source.slice(textStart, cursor),
                    range: { start: textStart, end: cursor },
                });
            }
            parts.push({ kind: 'skill', skillId, range: { start: cursor, end } });
            cursor = end;
            textStart = end;
            continue;
        }

        cursor++;
    }

    if (textStart < source.length)
    {
        parts.push({
            kind: 'text',
            text: source.slice(textStart),
            range: { start: textStart, end: source.length },
        });
    }

    return parts;
}

/** Converts scanner output into skill/input segments for a composer or stored skill prompt. */
export function parseSkillTokenSource(
    source: string,
    mode: SkillTokenParseMode,
): IParsedSkillTokenSource
{
    const parts = scanSkillTokens(source);
    const skills = parts.filter((part): part is Extract<ISkillTokenScanPart, { kind: 'skill'; }> =>
        part.kind === 'skill'
    );

    if (skills.length === 0)
    {
        return { source, mode, leadingText: source, invocations: [], parts };
    }

    const leadingRange = { start: 0, end: skills[ 0 ].range.start };
    const leadingText = source.slice(leadingRange.start, leadingRange.end).trim();
    if (mode === 'composer' && leadingText.length > 0)
    {
        throw new SkillTokenSyntaxError(
            'UNOWNED_LEADING_TEXT',
            leadingRange,
            'Text before the first skill token has no owning skill',
        );
    }

    const invocations = skills.map((skill, index): ISkillInvocationSegment =>
    {
        const inputRange = {
            start: skill.range.end,
            end: skills[ index + 1 ]?.range.start ?? source.length,
        };
        return {
            skillId: skill.skillId,
            inputText: source.slice(inputRange.start, inputRange.end).trim(),
            tokenRange: skill.range,
            inputRange,
        };
    });

    return { source, mode, leadingText, invocations, parts };
}

export function serializeSkillToken(skillId: number): string
{
    if (!Number.isSafeInteger(skillId) || skillId <= 0)
    {
        invalidSkillId(String(skillId), 0, String(skillId).length);
    }
    return `[skill:${ skillId }]`;
}

/** Produces a normalized canonical source while preserving invocation order and duplicates. */
export function serializeSkillInvocations(invocations: ISerializableSkillInvocation[]): string
{
    return invocations
        .map(({ skillId, inputText }) =>
        {
            const text = inputText?.trim() ?? '';
            return `${ serializeSkillToken(skillId) }${ text ? ` ${ text }` : '' }`;
        })
        .join(' ');
}
